import { TriageExplanation, TriageAIFeedback } from '@/types/triage';
import { supabase } from '@/integrations/supabase/client';

/**
 * Event interface for logging explanation view events
 */
interface ExplanationViewedEvent {
  triageId: string;
  patientId: string;
  explanation: TriageExplanation;
  timestamp: Date;
}

/**
 * Service for logging triage AI audit events
 * Complies with EU AI Act requirements for high-risk AI systems in healthcare
 */
class TriageAuditService {
  /**
   * Logs when a triage explanation is viewed by a clinician
   * Required for AI transparency and accountability
   */
  async logExplanationViewed(event: ExplanationViewedEvent): Promise<void> {
    try {
      // Validate required fields
      if (!event.triageId) {
        console.warn('AUDIT_SKIP: Triage ID is undefined. Skipping explanation_viewed audit log insertion.', event);
        return; // Skip insertion if triageId is missing - triage_id is NOT NULL in database
      }
      
      if (!event.patientId) {
        console.warn('AUDIT_SKIP: Patient ID is undefined. Skipping explanation_viewed audit log insertion.', event);
        return; // Skip insertion if patientId is missing - patient_id_hash is NOT NULL in database
      }
      
      // Ensure we have a valid patientId to hash
      const hashedPatientId = this.hashIdentifier(event.patientId);
      
      // Get user information safely
      let userId: string | undefined;
      let userRole: string | undefined;
      
      try {
        const userResponse = await supabase.auth.getUser();
        userId = userResponse.data.user?.id;
        userRole = userResponse.data.user?.user_metadata?.role;
      } catch (authError) {
        console.warn('AUDIT_WARNING: Could not retrieve user information', authError);
        // Continue without user info
      }
      
      // For logging in development
      console.log('AUDIT: Triage explanation viewed', {
        triageId: event.triageId,
        patientId: hashedPatientId, // Use hashed ID for privacy
        explanationFactors: event.explanation?.factors?.length || 0,
        confidence: event.explanation?.confidence || 0,
        timestamp: event.timestamp.toISOString(),
        userId: userId || 'unknown'
      });
      
      // Prepare the audit log entry with safe defaults for nullable fields
      const auditLogEntry = {
        id: this.generateUUID(),
        triage_id: event.triageId, // Required field - NOT NULL in database
        patient_id_hash: hashedPatientId,
        event_type: 'explanation_viewed',
        event_timestamp: event.timestamp.toISOString(),
        user_id: userId || null,
        user_role: userRole || null,
        metadata: {
          urgency: event.explanation?.urgency || 'unknown',
          confidence: event.explanation?.confidence || 0,
          factors_count: event.explanation?.factors?.length || 0
        }
      };
      
      // Debug log the actual data being sent to the database
      console.log('AUDIT_DEBUG: Sending to triage_audit_logs:', {
        triage_id: auditLogEntry.triage_id,
        event_type: auditLogEntry.event_type
      });
      
      const { error } = await supabase
        .from('triage_audit_logs')
        .insert(auditLogEntry);
      
      if (error) {
        console.error('Error inserting into triage_audit_logs:', error);
        // Log detailed error information for debugging
        console.error('Failed audit log entry details:', {
          triage_id_type: typeof event.triageId,
          triage_id_value: event.triageId,
          timestamp_type: typeof event.timestamp
        });
      } else {
        console.log('AUDIT: Successfully logged explanation viewed event');
      }
    } catch (err) {
      console.error('Error in logExplanationViewed:', err);
      // Non-blocking - we don't want to fail the UI if logging fails
    }
  }
  
  /**
   * Logs clinician feedback on AI triage explanations
   * Critical for ongoing system improvement and validation
   */
  async logAIFeedback(feedback: TriageAIFeedback): Promise<void> {
    try {
      // Validate required fields
      if (!feedback.triageId) {
        console.warn('AUDIT_SKIP: Triage ID is undefined. Skipping AI feedback audit log insertion.', feedback);
        return; // Skip insertion if triageId is missing - triage_id is NOT NULL in database
      }
      
      if (!feedback.patientId) {
        console.warn('AUDIT_SKIP: Patient ID is undefined. Skipping AI feedback audit log insertion.', feedback);
        return; // Skip insertion if patientId is missing - patient_id_hash is NOT NULL in database
      }
      
      const hashedPatientId = this.hashIdentifier(feedback.patientId);
      
      // Get user information safely
      let userId: string | undefined;
      let userRole: string | undefined;
      
      try {
        const userResponse = await supabase.auth.getUser();
        userId = userResponse.data.user?.id;
        userRole = userResponse.data.user?.user_metadata?.role;
      } catch (authError) {
        console.warn('AUDIT_WARNING: Could not retrieve user information', authError);
        // Continue without user info
      }
      
      // Extract panel ID from metadata if available
      const panelId = feedback.metadata?.panelId || 'unknown-panel';
      
      // For logging in development
      console.log('AUDIT: Clinician feedback received', {
        triageId: feedback.triageId,
        patientId: hashedPatientId, // Use hashed ID for privacy
        feedbackType: feedback.feedbackType,
        rating: feedback.rating,
        hasComments: !!feedback.comments,
        timestamp: feedback.timestamp?.toISOString() || new Date().toISOString(),
        panelId
      });
      
      // Insert into triage_ai_feedback table
      const { error: feedbackError } = await supabase
        .from('triage_ai_feedback')
        .insert({
          id: this.generateUUID(),
          triage_id: feedback.triageId,
          patient_id_hash: hashedPatientId,
          feedback_type: feedback.feedbackType,
          rating: feedback.rating,
          comments: feedback.comments,
          submitted_by: userId,
          submitted_at: feedback.timestamp?.toISOString() || new Date().toISOString(),
          clinician_role: userRole
        });
      
      if (feedbackError) {
        console.error('Error inserting into triage_ai_feedback:', feedbackError);
      }
      
      // Also log to audit logs for comprehensive tracking
      const { error: auditError } = await supabase
        .from('triage_audit_logs')
        .insert({
          id: this.generateUUID(),
          triage_id: feedback.triageId,
          patient_id_hash: hashedPatientId,
          event_type: 'feedback_submitted',
          event_timestamp: feedback.timestamp?.toISOString() || new Date().toISOString(),
          user_id: userId,
          user_role: userRole,
          feedback_type: feedback.feedbackType,
          feedback_rating: feedback.rating,
          feedback_comments: feedback.comments,
          metadata: {
            feedback_id: this.generateUUID(),
            source: 'triage_explanation_panel',
            panelId: panelId,
            ...feedback.metadata // Include any other metadata provided
          },
          created_by: userId
        });
      
      if (auditError) {
        console.error('Error inserting feedback event into triage_audit_logs:', auditError);
      }
      
    } catch (error) {
      console.error('Error logging AI feedback:', error);
    }
  }
  
  /**
   * Hashes patient identifiers for privacy protection
   * Ensures audit logs don't contain directly identifiable information
   * Uses browser-compatible approach instead of Node.js crypto
   */
  private hashIdentifier(identifier?: string): string {
    if (!identifier) {
      // Return a default hash or placeholder for undefined/empty identifiers
      return 'unknown-id-' + Date.now().toString(16);
    }
    try {
      // Use a simple hash function for browsers that works synchronously
      // Note: This is less secure than SHA-256 but works in all browsers
      // For production, consider using SubtleCrypto with proper async handling
      let hash = 0;
      for (let i = 0; i < identifier.length; i++) {
        const char = identifier.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      // Convert to hex string and ensure it's always positive
      return Math.abs(hash).toString(16).padStart(8, '0');
    } catch (error) {
      console.error('Error hashing identifier:', error);
      // Return a fallback hash to prevent errors
      return 'fallback-hash-' + Date.now().toString(16);
    }
  }

  /**
   * Generates a UUID using browser-compatible methods
   * Replacement for Node.js crypto.randomUUID()
   */
  private generateUUID(): string {
    // RFC4122 version 4 compliant UUID generator
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}

// Export a singleton instance
export const triageAuditService = new TriageAuditService();
